import {ReactNode} from "react";
import {Link} from "react-router-dom";
import {DibJsonNode, ResourceType} from "../../types";
import {bffApi} from "../../../chat/bffApi";
import {useTranslation} from "react-i18next";
import {
    buildFileDownloadHref,
    buildFileTopicUrl,
    fileColorMap,
    fileExtensionMap,
    fileIconMap,
} from "../../contentUtils";
import DownloadIcon from "../../../../assets/download.svg?react";
import contentLinkClasses from "../contentLink.module.css";
import classes from "./file.module.css";
import {metricUuid, newMetricEvent} from "../../../chat/metrics";

export const File = ({
    node,
    children,
}: {
    node: DibJsonNode;
    children: ReactNode[];
}) => {
    const {t} = useTranslation();
    const {data: userData} = bffApi.endpoints.getUser.useQueryState();
    const isDemo = !userData ? true : userData.demokonto;
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();

    const data = node.object!.data as {
        topicId: string;
        topicName: string;
        resourceTypeId: number;
    };
    const resourceTypeId = String(data.resourceTypeId);
    const resourceId = data.topicId;
    const fileName = data.topicName;
    const extension = fileExtensionMap[resourceTypeId] ?? "";
    const Icon = fileIconMap[resourceTypeId];
    const iconColor = fileColorMap[resourceTypeId];

    // A PDF opens in the in-app viewer instead of downloading, as a plain
    // topic link (no download metric).
    if (resourceTypeId === ResourceType.pdf) {
        return (
            <Link
                to={buildFileTopicUrl(fileName, resourceId)}
                className={`${contentLinkClasses.navLink} ${classes.fileLink}`}
            >
                {children}
                {Icon && (
                    <Icon
                        className={classes.icon}
                        style={iconColor ? {color: iconColor} : undefined}
                        aria-hidden
                    />
                )}
            </Link>
        );
    }

    const title = isDemo
        ? t("file.downloadNotForDemo")
        : t("file.download", {file: fileName});

    const href = isDemo ? undefined : buildFileDownloadHref(resourceId);

    return (
        <a
            href={href}
            download={`${fileName}${extension}`}
            title={title}
            className={`${contentLinkClasses.navLink} ${classes.fileLink} ${isDemo ? classes.disabled : ""}`}
            aria-disabled={isDemo || undefined}
            onClick={() => {
                if (isDemo) return;
                // A native anchor reveals only the user's request; browsers do
                // not expose whether it later received/saved the file.
                void trackInteraction({
                    ...newMetricEvent(),
                    action: "file_download_requested",
                    subjectId: metricUuid(resourceId),
                    source: "content",
                    format: "unknown",
                });
            }}
        >
            {children}
            {Icon && (
                <>
                    <Icon
                        className={classes.icon}
                        style={iconColor ? {color: iconColor} : undefined}
                        aria-hidden
                    />
                    <DownloadIcon className={classes.downloadIcon} aria-hidden />
                </>
            )}
        </a>
    );
};
